import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchAccessibleGroups } from "../../../../../store/features/groupsApi";
import {
  clearTicketRouting,
  clearTicketRoutingError,
  fetchTicketRoutingByGroup,
  updateTicketRoutingByGroup,
} from "../../../../../store/features/ticketRoutingApi";
import SettingsContent from "../../shared/SettingsContent";
import SettingsPageHeader from "../../shared/SettingsPageHeader";
import SettingsPrimaryButton from "../../shared/SettingsPrimaryButton";
import { getActiveGroups, getDefaultGroupId } from "../../shared/groupUtils";
import {
  buildRoutingPayload,
  getErrorMessage,
  mapRoutingFromApi,
  ROUTING_MODES,
} from "../TicketRouting/constants";
import TicketRoutingGroupSelect from "../TicketRouting/TicketRoutingGroupSelect";
import TicketRoutingImportantNotes from "../TicketRouting/TicketRoutingImportantNotes";
import TicketRoutingInfoCard from "../TicketRouting/TicketRoutingInfoCard";
import TicketRoutingOptions from "../TicketRouting/TicketRoutingOptions";

function TicketRoutingPage() {
  const dispatch = useDispatch();
  const { list: groups, listLoading: groupsLoading } = useSelector((state) => state.peopleGroups);
  const { teams, loading, saving, error, ticketRouting } = useSelector(
    (state) => state.ticketRouting,
  );

  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [routingMode, setRoutingMode] = useState(ROUTING_MODES.NONE);
  const [selectedTeamId, setSelectedTeamId] = useState("");

  const activeGroups = useMemo(() => getActiveGroups(groups), [groups]);
  const defaultGroupId = useMemo(() => getDefaultGroupId(groups), [groups]);
  const resolvedGroupId = selectedGroupId || defaultGroupId;

  useEffect(() => {
    dispatch(fetchAccessibleGroups());
    return () => {
      dispatch(clearTicketRouting());
    };
  }, [dispatch]);

  const loadRouting = useCallback(async () => {
    if (!resolvedGroupId) return;

    const result = await dispatch(fetchTicketRoutingByGroup({ groupId: resolvedGroupId }));

    if (fetchTicketRoutingByGroup.fulfilled.match(result)) {
      const mapped = mapRoutingFromApi(result.payload?.ticket_routing);
      setRoutingMode(mapped.mode);
      setSelectedTeamId(mapped.teamId);
    } else {
      toast.error(getErrorMessage(result.payload));
    }
  }, [dispatch, resolvedGroupId]);

  useEffect(() => {
    if (resolvedGroupId) {
      loadRouting();
    }
  }, [resolvedGroupId, loadRouting]);

  useEffect(() => {
    if (!error) return;
    toast.error(getErrorMessage(error));
    dispatch(clearTicketRoutingError());
  }, [error, dispatch]);

  useEffect(() => {
    if (!teams.length) {
      if (!loading) {
        setSelectedTeamId("");
      }
      return;
    }
    if (selectedTeamId && teams.some((team) => team.id === selectedTeamId)) {
      return;
    }
    const savedTeamId = ticketRouting?.team_id;
    if (savedTeamId && teams.some((team) => team.id === savedTeamId)) {
      setSelectedTeamId(savedTeamId);
      return;
    }
    setSelectedTeamId(teams[0].id);
  }, [teams, selectedTeamId, ticketRouting, loading]);

  const handleGroupChange = (groupId) => {
    setSelectedGroupId(groupId);
  };

  const handleSave = async () => {
    if (!resolvedGroupId) {
      toast.error("Please select a group.");
      return;
    }
    if (routingMode === ROUTING_MODES.ROUND_ROBIN_TEAM && !selectedTeamId) {
      toast.error("Please select a team.");
      return;
    }

    const result = await dispatch(
      updateTicketRoutingByGroup({
        groupId: resolvedGroupId,
        body: buildRoutingPayload(routingMode, selectedTeamId),
      }),
    );

    if (updateTicketRoutingByGroup.fulfilled.match(result)) {
      if (result.payload?.success === false) {
        toast.error(result.payload?.message || "Could not save routing settings.");
        return;
      }
      toast.success(result.payload?.message || "Routing settings saved.");
      loadRouting();
    } else {
      toast.error(getErrorMessage(result.payload));
    }
  };

  return (
    <SettingsContent>
      <SettingsPageHeader
        title="Ticket Routing Settings"
        description="Configure how new tickets are automatically assigned"
      />

      <TicketRoutingInfoCard />

      <TicketRoutingGroupSelect
        groups={activeGroups}
        value={resolvedGroupId}
        onChange={handleGroupChange}
        loading={groupsLoading}
      />

      <TicketRoutingOptions
        routingMode={routingMode}
        onRoutingModeChange={setRoutingMode}
        teams={teams}
        teamId={selectedTeamId}
        onTeamChange={setSelectedTeamId}
        teamsLoading={loading}
      />

      <TicketRoutingImportantNotes />

      <SettingsPrimaryButton
        type="button"
        onClick={handleSave}
        disabled={saving || groupsLoading || loading || !resolvedGroupId}
        sx={{ mt: "32px", width: 184, p: 0 }}
      >
        {saving ? (
          <CircularProgress size={18} sx={{ color: "#fff" }} />
        ) : (
          "Save Routing Settings"
        )}
      </SettingsPrimaryButton>
    </SettingsContent>
  );
}

export default TicketRoutingPage;
